import { useCallback, useState } from "react";
import {
  crearCurso,
  actualizarCurso,
  eliminarCurso,
  obtenerCurso,
} from "../../../../services/cursosService";
import { buildCursoPayload, normalizeCurso } from "../adapters/cursosAdapter";
import { initialCursoForm, validateCursoForm } from "../constants/cursos";
import { showToast } from "../../../../utils/showToast";
import useHorarios from "./useHorarios";

const buildCatalogoLabels = (form, { centros = [], niveles = [], especialidades = [] } = {}) => ({
  centro: centros.find((item) => String(item.id) === String(form.centro_id))?.nombre || "",
  nivel: niveles.find((item) => String(item.id) === String(form.nivel_id))?.nombre || "",
  especialidad:
    especialidades.find((item) => String(item.id) === String(form.especialidad_id))?.nombre || "",
});

const unwrapCursoResponse = (response) => response?.curso ?? response;

export default function useCursosForm({
  onCreated,
  onUpdated,
  onDeleted,
  centros = [],
  niveles = [],
  especialidades = [],
}) {
  const [modal, setModal] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [formNuevo, setFormNuevo] = useState(initialCursoForm);
  const [formEditar, setFormEditar] = useState(initialCursoForm);
  const [cursoEditando, setCursoEditando] = useState(null);
  const horarios = useHorarios();

  const cerrarModal = useCallback(() => {
    setModal("");
    setCursoEditando(null);
    setFormNuevo(initialCursoForm);
    setFormEditar(initialCursoForm);
    setDetalleLoading(false);
    horarios.loadHorarios(null);
  }, [horarios]);

  const abrirCrear = useCallback(() => {
    setFormNuevo(initialCursoForm);
    setCursoEditando(null);
    setModal("crear");
    horarios.loadHorarios(null);
  }, [horarios]);

  const abrirEditar = useCallback(async (curso) => {
    if (!curso?.id) return;
    if (typeof curso.id === "string" && curso.id.startsWith("temp-")) {
      setCursoEditando(normalizeCurso(curso));
        setFormEditar({
          nombre: curso.nombre || "",
          codigo_curso: curso.codigo_curso || curso.codigoCurso || "",
          centro_id: curso.centroId || curso.centro_id || "",
          nivel_id: curso.nivelId || curso.nivel_id || "",
          especialidad_id: curso.especialidadId || curso.especialidad_id || "",
          duracion_horas: curso.duracionHoras ?? curso.duracion_horas ?? "",
          precio_base: curso.precioBase ?? curso.precio_base ?? "",
          monto_min_reserva: curso.monto_min_reserva ?? curso.montoMinReserva ?? "",
          monto_matricula: curso.monto_matricula ?? curso.montoMatricula ?? "",
          activo: !!curso.activo,
          es_reentrenamiento: !!(curso.esReentrenamiento ?? curso.es_reentrenamiento),
          requiere_examen: !!(curso.requiereExamen ?? curso.requiere_examen),
          contifico_codigo: curso.contifico_codigo || curso.contificoCodigo || null,
          contifico_producto_id: curso.contifico_producto_id || curso.contificoProductoId || null,
          monto_comision: curso.monto_comision ?? curso.montoComision ?? "",
          curso_oficial_id: curso.cursoOficialId ?? curso.curso_oficial_id ?? "",
      });
      setModal("editar");
      return;
    }
    setModal("editar");
    setDetalleLoading(true);
    try {
      const data = await obtenerCurso(curso.id);
      const normalized = normalizeCurso(unwrapCursoResponse(data), curso);
      setCursoEditando(normalized);
      horarios.loadHorarios(curso.id);
      setFormEditar({
        nombre: normalized.nombre || "",
        codigo_curso: normalized.codigoCurso || "",
        centro_id: normalized.centroId || "",
        nivel_id: normalized.nivelId || "",
        especialidad_id: normalized.especialidadId || "",
        duracion_horas: normalized.duracionHoras ?? "",
        precio_base: normalized.precioBase ?? "",
        monto_min_reserva: normalized.montoMinReserva ?? "",
        monto_matricula: normalized.montoMatricula ?? "",
        activo: normalized.activo,
        es_reentrenamiento: normalized.esReentrenamiento,
        requiere_examen: normalized.requiereExamen,
        contifico_codigo: normalized.contificoCodigo || null,
        contifico_producto_id: normalized.contificoProductoId || null,
        monto_comision: normalized.montoComision ?? "",
        curso_oficial_id: normalized.cursoOficialId ?? "",
      });
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo cargar el curso seleccionado." });
      setModal("");
    } finally {
      setDetalleLoading(false);
    }
  }, [horarios]);

  const abrirEliminar = useCallback((curso) => {
    setCursoEditando(normalizeCurso(curso));
    setModal("eliminar");
  }, []);

  const handleCrear = useCallback(async () => {
    const validation = validateCursoForm(formNuevo);
    if (validation) {
      showToast({ type: "warning", message: validation });
      return;
    }
    setSaving(true);
    try {
      const payload = buildCursoPayload(formNuevo);
      const creado = unwrapCursoResponse(await crearCurso(payload));
      const labels = buildCatalogoLabels(formNuevo, { centros, niveles, especialidades });
      const normalized = normalizeCurso({ ...formNuevo, ...labels, ...creado });
      onCreated?.(normalized);
      const cursoId = normalized?.id;
      if (!cursoId) {
        showToast({
          type: "warning",
          message: "Curso creado, pero no se recibio el identificador para habilitar horarios.",
        });
        cerrarModal();
        return;
      }
      setCursoEditando(normalized);
      setFormEditar({
        nombre: normalized.nombre || "",
        codigo_curso: normalized.codigoCurso || "",
        centro_id: normalized.centroId || "",
        nivel_id: normalized.nivelId || "",
        especialidad_id: normalized.especialidadId || "",
        duracion_horas: normalized.duracionHoras ?? "",
        precio_base: normalized.precioBase ?? "",
        monto_min_reserva: normalized.montoMinReserva ?? "",
        monto_matricula: normalized.montoMatricula ?? "",
        activo: normalized.activo,
        es_reentrenamiento: normalized.esReentrenamiento,
        requiere_examen: normalized.requiereExamen,
        contifico_codigo: normalized.contificoCodigo || null,
        contifico_producto_id: normalized.contificoProductoId || null,
        monto_comision: normalized.montoComision ?? "",
        curso_oficial_id: normalized.cursoOficialId ?? "",
      });
      setFormNuevo(initialCursoForm);
      horarios.loadHorarios(cursoId);
      setModal("editar");
      showToast({ type: "success", message: "Curso creado. Ya puedes agregar horarios." });
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo crear el curso." });
    } finally {
      setSaving(false);
    }
  }, [formNuevo, cerrarModal, onCreated, centros, niveles, especialidades, horarios]);

  const handleActualizar = useCallback(async () => {
    if (!cursoEditando) return;
    const validation = validateCursoForm(formEditar);
    if (validation) {
      showToast({ type: "warning", message: validation });
      return;
    }
    setSaving(true);
    try {
      const payload = buildCursoPayload(formEditar);
      const actualizado = unwrapCursoResponse(await actualizarCurso(cursoEditando.id, payload));
      const labels = buildCatalogoLabels(formEditar, { centros, niveles, especialidades });
      const normalized = normalizeCurso({ ...cursoEditando, ...formEditar, ...labels, ...actualizado });
      onUpdated?.(normalized);
      showToast({ type: "success", message: "Curso actualizado correctamente." });
      horarios.loadHorarios(cursoEditando.id);
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo actualizar el curso." });
    } finally {
      setSaving(false);
    }
  }, [cursoEditando, formEditar, cerrarModal, onUpdated, centros, niveles, especialidades, horarios]);

  const handleEliminar = useCallback(async () => {
    if (!cursoEditando) return;
    setDeleting(true);
    try {
      await eliminarCurso(cursoEditando.id);
      onDeleted?.(cursoEditando);
      showToast({ type: "success", message: "Curso eliminado correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo eliminar el curso." });
    } finally {
      setDeleting(false);
    }
  }, [cursoEditando, cerrarModal, onDeleted]);

  return {
    modal,
    formNuevo,
    setFormNuevo,
    formEditar,
    setFormEditar,
    cursoEditando,
    saving,
    deleting,
    detalleLoading,
    abrirCrear,
    abrirEditar,
    abrirEliminar,
    cerrarModal,
    handleCrear,
    handleActualizar,
    handleEliminar,
    horarios,
  };
}
